import classnames from 'classnames'
import { useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import {
  Filter,
  SelectedFilter,
  FilterOptions,
  UpdateQueryParams,
  FilterKeys,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import Select, { SelectOptionProps } from '../../select'
import Skeleton from '../../skeleton/skeleton.component'
import StickyWrapper from '../../stickyWrapper'
import { SORT_BY, SORT_OPTIONS } from '../constants/constants'
import { filterListOnExp, useShouldHideDeptAndCategoryFilter } from '../filterListOnExp'
import Cross from '../icons/cross.inline.svg'
import Menu from '../icons/menu.inline.svg'
import FilterModal from '../modal'

import styles from './filterSection.module.scss'
import { excludeDepartmentFilter } from './filterUtils'

const MAX_FILTERS = 8

export interface FilterSectionProps {
  className?: string
  filters: Filter[]
  noOfItems: number
  selectedFilters: SelectedFilter
  sortBy: string
  showSelectedOption?: boolean
  shouldRenderColorFilterHtml?: boolean
  collectionSlug?: string
  hideFilters?: boolean
  skipSorting?: boolean
  updateQuery: (params: UpdateQueryParams) => void
  showShimmer?: boolean
}

const FilterSection = ({
  className,
  filters = [],
  noOfItems,
  selectedFilters,
  sortBy,
  showSelectedOption,
  shouldRenderColorFilterHtml = false,
  collectionSlug,
  hideFilters,
  skipSorting,
  updateQuery,
  showShimmer,
}: FilterSectionProps) => {
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const [isModalOpen, setIsModalOpen] = useState(false)
  const innerStyle = {
    minHeight: SelectedFilter.isAnyFilterSelected(selectedFilters) ? 'auto' : isDesktop ? '6.8rem' : '4.9rem',
  }

  const containerStyle = {
    minHeight: isDesktop ? '6.8rem' : '4.9rem',
  }
  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()
  const _filters = filterListOnExp(excludeDepartmentFilter(filters, collectionSlug), hideDeptAndCategoryFilter)
    .filter(item => item.values.length > 1)
    .slice(0, MAX_FILTERS)

  const renderFilter = (isIntersecting: boolean) => {
    const sendSortOpenEvent = () => {
      trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
    }
    const noOfFiltersApplied =
      SelectedFilter.getSelectedValuesAsList(selectedFilters).length +
      (sortBy && sortBy !== SORT_BY.FEATURED.value ? 1 : 0)

    return (
      <>
        <div
          className={classnames(styles.filterSection, className, {
            [styles.filterSection__hideBorderTop]: !isIntersecting,
          })}
          style={innerStyle}
          // id="collectionFilterSection"
        >
          <div className={styles.container} style={containerStyle}>
            <div className={styles.container__wrapper}>
              <div className={styles.container__wrapper__left}>
                {!hideFilters && (
                  <>
                    <Menu
                      width={24}
                      onClick={() => {
                        setIsModalOpen(true)
                      }}
                      className={styles.container__wrapper__left__sign}
                    />
                    {noOfFiltersApplied > 0 && (
                      <div className={styles.container__wrapper__left__count}>
                        {'(' + noOfFiltersApplied.toString() + ')'}
                      </div>
                    )}
                    {_filters
                      .filter(x => x.values.length > 0)
                      .map(filter => {
                        return (
                          <Select
                            key={filter.name}
                            id={filter.name}
                            placeholder={filter.config.label}
                            options={getOptions(
                              filter.values,
                              filter.config.label,
                              filter.name,
                              shouldRenderColorFilterHtml,
                              collectionSlug ?? '',
                            )}
                            hiddenOptionContainer={shouldRenderColorFilterHtml}
                            showMultipleColumns={false}
                            className={styles.container__wrapper__left__filter}
                            OnOptionClick={(_e, optionValue) => {
                              updateQuery({
                                filter: {
                                  type: 'toggle',
                                  name: filter.name as FilterKeys,
                                  value: optionValue,
                                  label: filter.config.label,
                                },
                              })
                            }}
                            selectedOptions={SelectedFilter.getSelectedFilterValuesByFilterName(
                              selectedFilters,
                              filter.name as FilterKeys,
                            )}
                          />
                        )
                      })}
                  </>
                )}
              </div>
              <div className={styles.container__wrapper__right}>
                {showShimmer ? (
                  <Skeleton height={18} width={80} />
                ) : (
                  <div
                    data-testid={TEST_LOCATORS.COLLECTION_PRODUCT_COUNT}
                    className={styles.container__wrapper__right__items}
                  >
                    {noOfItems} ITEMS
                  </div>
                )}
                {!hideFilters && !skipSorting && (
                  <Select
                    id="Sort"
                    selectedOptions={[sortBy]}
                    className={styles.container__wrapper__right__sort}
                    placeholder="Sort"
                    options={SORT_OPTIONS}
                    OnOptionClick={(_e, value) => {
                      updateQuery({ sort: { type: 'toggle', value } })
                    }}
                    showSelectedOptionOnDropDown={showSelectedOption}
                    showSelectedOptionOnLabel={showSelectedOption}
                    sendSortOpenEvent={sendSortOpenEvent}
                    isSortOption
                  />
                )}
              </div>
            </div>
          </div>
          {SelectedFilter.isAnyFilterSelected(selectedFilters) && (
            <div className={styles.container__filters} data-testid={TEST_LOCATORS.FILTER_SECTION_APPLIED_FILTERS}>
              {Object.entries(selectedFilters).flatMap(([filterName, filterValues]) => {
                if (!filterValues) return []
                return filterValues.map((filterValueItem: FilterKeys) => (
                  <div
                    key={`${filterName}-${filterValueItem}`}
                    data-applied-value={filterValueItem}
                    className={styles.container__filters__item}
                  >
                    <div className={styles.container__filters__item__text}>{filterValueItem}</div>
                    <Cross
                      data-icon="cross"
                      className={styles.container__filters__item__sign}
                      onClick={() => {
                        updateQuery({
                          filter: { type: 'remove', name: filterName as FilterKeys, value: filterValueItem },
                        })
                      }}
                    />
                  </div>
                ))
              })}
              {SelectedFilter.getSelectedValuesAsList(selectedFilters).length > 0 && (
                <div
                  className={styles.container__filters__clear}
                  data-testid={TEST_LOCATORS.FILTER_SECTION_CLEAR_ALL}
                  onClick={() => {
                    updateQuery({ filter: { type: 'reset' }, sort: { type: 'reset' } })
                  }}
                >
                  <div className={styles.container__filters__clear__text}>CLEAR ALL</div>
                  <Cross className={styles.container__filters__clear__sign} />
                </div>
              )}
            </div>
          )}
        </div>
        <FilterModal
          open={isModalOpen}
          filters={filters}
          noOfItems={noOfItems}
          onCancel={() => {
            setIsModalOpen(false)
          }}
          selectedFilters={selectedFilters}
          updateQuery={updateQuery}
          showSelectedOptionOnDropDown={showSelectedOption}
          skipSorting={skipSorting}
          sortBy={sortBy}
        />
      </>
    )
  }

  return (
    <div id={'stickyWrapperNew'}>
      <StickyWrapper renderChildren={renderFilter} stickyWrapperClassName={isModalOpen ? styles.stickyWrapper : ''} />
    </div>
  )
}

const getOptions = (
  options: (string | FilterOptions)[],
  filterLabel: string,
  filterName: string,
  shouldRenderColorFilterHtml: boolean | undefined,
  collectionSlug: string,
) => {
  // TODO: @tech-debt 1. prevent filters fetch on filter/sortBy updates 2. Make SAND team provide consistent colors data
  const formattedOptions = options.map(Filter.makeSelectFilterOptionFromValue.bind(null))
  if (filterName === 'colors') {
    formattedOptions.sort((a, b) => a.value.localeCompare(b.value))
  }
  return formattedOptions.map((selectOptionProp: SelectOptionProps) => {
    return shouldRenderColorFilterHtml && filterLabel === 'COLOR'
      ? {
          ...selectOptionProp,
          isLink: true,
          linkPath: filterName === 'colors' ? `${collectionSlug}/color/${selectOptionProp.value.toLowerCase()}` : '',
        }
      : selectOptionProp
  })
}

export default FilterSection
